import type {
  ApiErrorBody,
  CreateFolderBody,
  CreateNoteBody,
  CreateReminderBody,
  CreateTodoBody,
  Folder,
  FolderList,
  Note,
  NoteList,
  PushKey,
  PushSubscriptionBody,
  Reminder,
  ReminderList,
  SearchResults,
  SessionUser,
  SetupState,
  TagList,
  Todo,
  TodoList,
  UpdateFolderBody,
  UpdateNoteBody,
  UpdateReminderBody,
  UpdateTodoBody,
} from '@kanto/shared';
import { activeLocale } from '../lib/i18n/locale';

/**
 * A refusal from the API, carrying its HTTP status.
 *
 * The status is what separates "you are signed out" from "something broke": a
 * 401 on `GET /api/auth/me` is the ordinary answer for a visitor who has not
 * signed in, and treating it as a failure would retry it and delay the form.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    headers: {
      // The language chosen in the switcher, not the one the browser would send
      // by itself: the server writes its refusals in what it reads here, so a
      // page switched to French must not receive its next error in English.
      'Accept-Language': activeLocale(),
      ...(typeof init?.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
    // The session cookie is `httpOnly` and lives on this origin. Nothing here
    // reads it — `GET /api/auth/me` is the only way to know who is signed in.
    credentials: 'same-origin',
  });

  if (!response.ok) {
    // Every refusal has the `ApiErrorBody` shape, but a proxy or a crash can
    // still answer with something else, so the parse is allowed to fail.
    const body = (await response.json().catch(() => null)) as Partial<ApiErrorBody> | null;
    throw new ApiError(
      response.status,
      body?.error ?? 'unknown',
      body?.message ?? `Error ${response.status}`,
    );
  }

  return (await response.json()) as T;
}

export const api = {
  me: () => request<SessionUser>('/auth/me'),

  /** Public: says whether the instance still has no account at all. */
  setupState: () => request<SetupState>('/auth/setup-state'),

  login: (username: string, password: string) =>
    request<SessionUser>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),

  /**
   * The todos of whoever is signed in. There is no owner in any of these calls:
   * the server reads it from the session, and a front end that could name one
   * would be a front end that could name somebody else's.
   *
   * Everything but the list is a `POST`, including archiving — the row survives
   * it, so there is nothing to `DELETE`.
   */
  todos: {
    list: () => request<TodoList>('/todos'),

    create: (body: CreateTodoBody) =>
      request<Todo>('/todos', { method: 'POST', body: JSON.stringify(body) }),

    update: (id: string, body: UpdateTodoBody) =>
      request<Todo>(`/todos/${encodeURIComponent(id)}`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    archive: (id: string) =>
      request<{ ok: true }>(`/todos/${encodeURIComponent(id)}/archive`, { method: 'POST' }),

    restore: (id: string) =>
      request<Todo>(`/todos/${encodeURIComponent(id)}/restore`, { method: 'POST' }),
  },

  /** The same five calls as `todos`, against the module that rings. */
  reminders: {
    list: () => request<ReminderList>('/reminders'),

    create: (body: CreateReminderBody) =>
      request<Reminder>('/reminders', { method: 'POST', body: JSON.stringify(body) }),

    update: (id: string, body: UpdateReminderBody) =>
      request<Reminder>(`/reminders/${encodeURIComponent(id)}`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    archive: (id: string) =>
      request<{ ok: true }>(`/reminders/${encodeURIComponent(id)}/archive`, { method: 'POST' }),

    restore: (id: string) =>
      request<Reminder>(`/reminders/${encodeURIComponent(id)}/restore`, { method: 'POST' }),
  },

  /**
   * Notes. Six calls rather than five, because the body does not travel with the
   * list: `list` answers summaries and `get` is what carries the one being read.
   *
   * There is no `move`. `folderId` on `update` files a note, and `null` puts it
   * back in the inbox.
   */
  notes: {
    list: () => request<NoteList>('/notes'),

    get: (id: string) => request<Note>(`/notes/${encodeURIComponent(id)}`),

    create: (body: CreateNoteBody) =>
      request<Note>('/notes', { method: 'POST', body: JSON.stringify(body) }),

    update: (id: string, body: UpdateNoteBody) =>
      request<Note>(`/notes/${encodeURIComponent(id)}`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    archive: (id: string) =>
      request<{ ok: true }>(`/notes/${encodeURIComponent(id)}/archive`, { method: 'POST' }),

    restore: (id: string) =>
      request<Note>(`/notes/${encodeURIComponent(id)}/restore`, { method: 'POST' }),
  },

  /**
   * The tree the notes are filed into. `delete` is the only call in this whole
   * client that removes a row: a folder is structure rather than content, and an
   * empty one holds nothing to archive.
   */
  folders: {
    list: () => request<FolderList>('/folders'),

    create: (body: CreateFolderBody) =>
      request<Folder>('/folders', { method: 'POST', body: JSON.stringify(body) }),

    update: (id: string, body: UpdateFolderBody) =>
      request<Folder>(`/folders/${encodeURIComponent(id)}`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    remove: (id: string) =>
      request<{ ok: true }>(`/folders/${encodeURIComponent(id)}/delete`, { method: 'POST' }),
  },

  /**
   * The account's tag registry, read-only. Its own family and not a sub-path of
   * either list, because the vocabulary belongs to the account and is offered by
   * every module that carries a tag.
   */
  tags: {
    list: () => request<TagList>('/tags'),
  },

  /**
   * One call across every module.
   *
   * The query goes over as the person typed it, punctuation and all: the server
   * is what turns it into something FTS5 accepts, and a front end that tried to
   * would be a second grammar to keep in step with the first (D260815f).
   */
  search: {
    run: (query: string) =>
      request<SearchResults>(`/search?${new URLSearchParams({ q: query }).toString()}`),
  },

  /**
   * Which devices may be rung. Its own family and not a sub-path of reminders: a
   * subscription belongs to this browser profile and this account, and outlives
   * every reminder that prompted it.
   */
  push: {
    key: () => request<PushKey>('/push/key'),

    subscribe: (body: PushSubscriptionBody) =>
      request<{ ok: true }>('/push/subscribe', { method: 'POST', body: JSON.stringify(body) }),

    unsubscribe: (endpoint: string) =>
      request<{ ok: true }>('/push/unsubscribe', {
        method: 'POST',
        body: JSON.stringify({ endpoint }),
      }),
  },
};

/**
 * The text to show for a failed call.
 *
 * A server message is written for the person reading it and already translated
 * into the language the request announced, so it always wins. The fallback is
 * for the one case where no message exists: the request never reached the
 * server.
 */
export function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}
